/**
 * Hook for publishing a composition
 * Handles the full publish flow: meeting creation, uploads, section creation, trims, publish
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { publishComposition } from '../lib/publish';
import { useUiContext } from '../../../context/UiContext';
import { useDirtyHelpers } from './useUnsavedChanges';

export default function usePublish() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notifyError } = useUiContext();
  const { markClean } = useDirtyHelpers();
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState({
    currentSection: 0,
    totalSections: 0,
    percentage: 0,
  });

  const publish = async ({
    title,
    folderId,
    comment,
    assets,
    timelineItems,
    meetingId: existingMeetingId,
    fetchedSections,
    pendingSaveItemIds,
  }) => {
    if (isPublishing) {
      return undefined;
    }

    setIsPublishing(true);
    setPublishProgress({
      currentSection: 0,
      totalSections: 0,
      percentage: 0,
    });

    try {
      const meeting = await publishComposition({
        title,
        folderId,
        comment,
        assets,
        timelineItems,
        meetingId: existingMeetingId,
        fetchedSections,
        pendingSaveItemIds,
        onProgress: (currentSection, totalSections, percentage) => {
          setPublishProgress({
            currentSection,
            totalSections,
            percentage,
          });
        },
      });

      // Invalidate React Query cache for the meeting and sections
      // This ensures that when user navigates back to the editor, fresh data is loaded
      if (meeting?.id) {
        queryClient.invalidateQueries(['meeting', meeting.id]);
        queryClient.invalidateQueries(['sections', meeting.id]);
      }
      // Also invalidate for existingMeetingId if it's different from meeting.id
      if (existingMeetingId && existingMeetingId !== meeting?.id) {
        queryClient.invalidateQueries(['meeting', existingMeetingId]);
        queryClient.invalidateQueries(['sections', existingMeetingId]);
      }

      markClean();

      // Invalidate meetings list cache (My Videos page)
      // This ensures the version number is updated in the list after publishing
      queryClient.invalidateQueries(['meetings']);

      // Invalidate folders cache (My Library page)
      // This ensures the version number is updated in the library list after publishing
      queryClient.invalidateQueries(['folders']);

      // Navigate to meeting or library
      const meetingFolderId = meeting?.folderId || folderId;
      if (meetingFolderId) {
        navigate(`/my-library/${meetingFolderId}`);
      } else {
        navigate('/my-videos');
      }

      return meeting;
    } catch (error) {
      console.error('Publish error:', error);
      // Check error.message first (for our thrown errors), then API response message
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to publish video';
      notifyError(errorMessage);
      throw error;
    } finally {
      setIsPublishing(false);
      setPublishProgress({
        currentSection: 0,
        totalSections: 0,
        percentage: 0,
      });
    }
  };

  return {
    publish,
    isPublishing,
    publishProgress,
  };
}

